import { useCommunityOptions } from '@communities/community/hooks';
import { Select, MultiSelect, Shimmer } from '@soteria/design-system-ve';

import * as styles from './CommunityDialog.css';

interface ContentInfoSectionProps {
  pgRating: string;
  setPgRating: (value: string) => void;
  contentGenres: string[];
  setContentGenres: (value: string[]) => void;
  disabled?: boolean;
}

export function ContentInfoSection({ pgRating, setPgRating, contentGenres, setContentGenres, disabled = false }: ContentInfoSectionProps) {
  const { genres, contentRatings, loading } = useCommunityOptions();

  // Convert ratings to the format expected by Select
  const ratingOptions = contentRatings.map(rating => ({
    value: rating.value,
    label: rating.name,
    description: rating.description,
  }));

  // Filter out the "None" option from genres and convert remaining genres to the format expected by MultiSelect
  const genreOptions = genres
    .filter(genre => genre.value.toLowerCase() !== 'none' && genre.name.toLowerCase() !== 'none')
    .map(genre => ({
      id: genre.value,
      label: genre.name,
      value: genre.value,
      description: genre.description,
    }));

  // Display text for the genre placeholder
  const genrePlaceholderText = contentGenres.length === 0 ? 'None' : 'Select content genres';

  return (
    <div className={styles.formSection}>
      <div className={styles.formGroupRow}>
        {loading ? (
          <>
            {/* Shimmer for Content Rating */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Shimmer label="Loading content rating" style={{ width: '120px', height: '14px', borderRadius: '4px' }} />
              <Shimmer label="Loading content rating input" style={{ width: '100%', height: '40px', borderRadius: '6px' }} />
            </div>
            {/* Shimmer for Content Genres */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Shimmer label="Loading content genres" style={{ width: '120px', height: '14px', borderRadius: '4px' }} />
              <Shimmer label="Loading content genres input" style={{ width: '100%', height: '40px', borderRadius: '6px' }} />
            </div>
          </>
        ) : (
          <>
            {/* Content Rating Selector */}
            <Select
              required
              disabled={disabled}
              emptyMessage="No ratings available"
              label="Content Rating"
              options={ratingOptions}
              placeholder="Select content rating"
              value={pgRating}
              onChange={setPgRating}
            />

            {/* Content Genres Selector */}
            <MultiSelect
              disabled={disabled}
              options={genreOptions}
              placeholder={genrePlaceholderText}
              selectedValues={contentGenres}
              title="Content Genres"
              onSelectionChange={setContentGenres}
            />
          </>
        )}
      </div>
    </div>
  );
}
